import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Heart, MapPin, Phone, Mail, Facebook, Clock } from "lucide-react";
import { AdminPanel } from "@/components/AdminPanel";
import { useAdminAuth } from "@/components/AdminAuth";

export const Footer = () => {
  const { isAdminLoggedIn } = useAdminAuth();
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  return (
    <footer className="bg-foreground text-background">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-white font-display font-bold text-lg">
                C
              </div>
              <div>
                <h3 className="font-display font-bold text-lg text-white">
                  Château de Clémentine
                </h3>
                <p className="text-xs text-muted-foreground">Depuis 1993</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Plus de 30 ans d'expérience dans la location de châteaux gonflables 
              et sumos en Belgique. Votre partenaire pour des fêtes inoubliables !
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Navigation</h4>
            <nav className="space-y-2">
              {[
                { label: "Accueil", href: "#accueil" },
                { label: "Châteaux Gonflables", href: "#chateaux" },
                { label: "Sumos", href: "#sumos" },
                { label: "Pourquoi Nous", href: "#pourquoi" },
                { label: "Contact", href: "#contact" }
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="block text-sm text-muted-foreground hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white mb-4">Nos Services</h4>
            <div className="space-y-2">
              {[
                "Châteaux Gonflables",
                "Sumos",
                "Retrait Rhode-Saint-Genèse",
                "Événements Privés",
                "Fêtes d'Entreprise",
                "Devis Gratuit"
              ].map((service, index) => (
                <div key={index} className="text-sm text-muted-foreground">
                  {service}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <div className="space-y-3">
              
              <div className="flex items-start space-x-3">
                <Mail className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                <div>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="h-auto p-0 text-sm text-white hover:text-secondary font-normal"
                    asChild
                  >
                    <a href="mailto:lechateaudeclementine@gmail.com?subject=Demande%20de%20renseignements">
                      lechateaudeclementine@gmail.com
                    </a>
                  </Button>
                  <p className="text-xs text-muted-foreground">Réponse sous 24h</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-white">Rhode-Saint-Genèse</p>
                  <p className="text-xs text-muted-foreground">Point de retrait</p>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-muted-foreground text-center md:text-left">
              © 2024 Château de Clémentine. Tous droits réservés. 
              <br className="xs:hidden" />
              <span className="mx-2 hidden xs:inline">•</span>
              <span className="inline-flex items-center">
                Fait avec <Heart className="w-3 h-3 text-primary mx-1" fill="currentColor" /> en Belgique
              </span>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end gap-4 xs:gap-6 text-sm text-muted-foreground">
              <a href="/terms-of-service" className="hover:text-white transition-colors min-h-[44px] flex items-center">
                <span className="xs:hidden">CGV</span>
                <span className="hidden xs:inline">Conditions Générales</span>
              </a>
              <a href="/privacy-policy" className="hover:text-white transition-colors min-h-[44px] flex items-center">
                <span className="xs:hidden">Confidentialité</span>
                <span className="hidden xs:inline">Politique de Confidentialité</span>
              </a>
              <a href="/cookie-policy" className="hover:text-white transition-colors min-h-[44px] flex items-center">
                <span className="xs:hidden">Cookies</span>
                <span className="hidden xs:inline">Politique des Cookies</span>
              </a>
              <a href="/faq" className="hover:text-white transition-colors min-h-[44px] flex items-center">
                FAQ
              </a>
              {isAdminLoggedIn ? (
                <button 
                  onClick={() => setIsAdminOpen(true)} 
                  className="hover:text-white transition-colors min-h-[44px] flex items-center"
                >
                  Admin Panel
                </button>
              ) : (
                <button 
                  onClick={() => setIsAdminOpen(true)} 
                  className="hover:text-white transition-colors min-h-[44px] flex items-center"
                >
                  Admin
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <AdminPanel 
        isOpen={isAdminOpen} 
        onClose={() => setIsAdminOpen(false)} 
      />
    </footer>
  );
};